import * as React from 'react'
import { Loader2, Lock } from 'lucide-react'
import { toast } from 'sonner'
import {
  useListBranchesQuery,
  useSwitchBranch,
  type Branch
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'
import { BranchCreateDialog } from './BranchCreateDialog'

export interface ProtectedBranchPromptDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The protected branch the edit attempted to write (main or any protected branch). */
  branch?: Branch
}

/**
 * The prompt-to-branch flow: an edit attempted while the active branch is
 * protected lands here BEFORE any write is sent (call sites guard via
 * useProtectedBranchGuard); the proxy's structural 409 is the backstop for
 * anything that slips through. The user explicitly chooses where the work
 * lives — switch to an existing writable branch, or create a new one (which
 * opens and becomes active) — nothing is created silently.
 */
export function ProtectedBranchPromptDialog({
  open,
  onOpenChange,
  branch
}: ProtectedBranchPromptDialogProps) {
  // The writable targets are user-facing branches (user/agent); the system /
  // ingest population is machine-managed and not where a person edits, so the
  // user-population page is the right, bounded candidate set.
  const { data } = useListBranchesQuery({ origin: 'user' })
  const branches = data?.branches
  const { switchBranch } = useSwitchBranch()
  const [createOpen, setCreateOpen] = React.useState(false)
  const [switchingId, setSwitchingId] = React.useState<string | null>(null)

  const writable = React.useMemo(
    () => (branches ?? []).filter((b) => b.status === 'active' && !b.protected),
    [branches]
  )
  const branchName = branch?.name ?? 'main'

  const handleSwitch = async (target: Branch) => {
    setSwitchingId(target.id)
    try {
      await switchBranch(target.id)
      toast.success(`Switched to ${target.name}`)
      onOpenChange(false)
    } catch {
      toast.error(`Could not open ${target.name}`)
    } finally {
      setSwitchingId(null)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="size-4" />
              Branch is protected
            </DialogTitle>
            <DialogDescription>
              <span className="font-mono text-ink">{branchName}</span> is protected — its graph only
              changes through merges. Pick a branch for this edit, or create one.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={writable.length === 0 || switchingId !== null}>
                  {switchingId !== null ? <Loader2 className="size-4 animate-spin" /> : null}
                  {writable.length === 0 ? 'No writable branches yet' : 'Switch to a branch…'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="max-h-64 overflow-y-auto">
                {writable.map((b) => (
                  <DropdownMenuItem key={b.id} onSelect={() => void handleSwitch(b)}>
                    <span className="font-mono text-xs">{b.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={() => setCreateOpen(true)}>Create branch…</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <BranchCreateDialog
        open={createOpen}
        onOpenChange={(next) => {
          setCreateOpen(next)
          // Creating + switching resolved the prompt — close it too.
          if (!next) onOpenChange(false)
        }}
        source={branch}
      />
    </>
  )
}

/**
 * Pre-flight guard for manual graph-write affordances. `ensureWritable()`
 * returns true when the active branch accepts writes; otherwise it opens the
 * prompt dialog and returns false — call it first in any mutating handler:
 *
 *   if (!guard.ensureWritable()) return
 *
 * Render `guard.dialog` once alongside the surface.
 */
export function useProtectedBranchGuard(activeRecord: Branch | undefined): {
  ensureWritable: () => boolean
  dialog: React.ReactNode
} {
  const [promptOpen, setPromptOpen] = React.useState(false)

  const ensureWritable = React.useCallback(() => {
    // An unknown branch row (list still loading) fails safe to the prompt:
    // the server would refuse the write anyway.
    if (activeRecord !== undefined && !activeRecord.protected) return true
    setPromptOpen(true)
    return false
  }, [activeRecord])

  return {
    ensureWritable,
    dialog: (
      <ProtectedBranchPromptDialog
        open={promptOpen}
        onOpenChange={setPromptOpen}
        branch={activeRecord}
      />
    )
  }
}
